import React from "react";
import { twMerge } from "tailwind-merge";
import { Heading } from "../Heading";
import { Skeleton } from "../Skeleton";

/**
 * Card - Enterprise surface container component
 *
 * A flexible, composable card component following enterprise design system best practices.
 * Supports modular content sections with consistent styling and accessibility.
 *
 * Usage:
 * ```tsx
 * <Card variant="outlined" size="md">
 *   <Card.Header
 *     title="Card Title"
 *     subtitle="Optional subtitle"
 *     actions={<Button variant="ghost" icon="MoreHorizontal" />}
 *   />
 *   <Card.Media src="image.jpg" alt="Description" />
 *   <Card.Content>
 *     <p>Main content goes here</p>
 *   </Card.Content>
 *   <Card.Footer>
 *     <Button variant="brand">Action</Button>
 *   </Card.Footer>
 * </Card>
 * ```
 */

// ——————————————————————————————————————————
// Types
// ——————————————————————————————————————————

export type CardVariant = "outlined" | "elevated" | "filled" | "ghost";
export interface CardProps {
  variant?: CardVariant;
  className?: string;
  children?: React.ReactNode;
  isLoading?: boolean;
  /** Enable flex column layout so children can expand to fill space */
  layout?: "default" | "flex";
}

export interface CardHeaderProps {
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
  className?: string;
  isLoading?: boolean;
}

export interface CardMediaProps {
  src?: string;
  alt?: string;
  aspectRatio?: "square" | "video" | "wide" | "auto";
  className?: string;
  children?: React.ReactNode;
  isLoading?: boolean;
}

export interface CardContentProps {
  className?: string;
  children?: React.ReactNode;
  isLoading?: boolean;
  /** When true and parent Card has layout="flex", content expands to fill available space */
  fill?: boolean;
}

export interface CardFooterProps {
  className?: string;
  children?: React.ReactNode;
  align?: "start" | "end" | "center" | "between";
  isLoading?: boolean;
}

// ——————————————————————————————————————————
// Styles
// ——————————————————————————————————————————

const cardVariantStyles: Record<CardVariant, string> = {
  outlined: "bg-background-surface border border-border-default",
  elevated: "bg-background-surface border-0 shadow-md",
  filled: "bg-background-muted border-0",
  ghost: "bg-transparent border-0 shadow-none",
};

const cardBaseStyles = "rounded-lg";

const aspectRatioStyles = {
  square: "aspect-square",
  video: "aspect-video",
  wide: "aspect-[2/1]",
  auto: "",
};

const footerAlignStyles = {
  start: "justify-start",
  end: "justify-end",
  center: "justify-center",
  between: "justify-between",
};

// ——————————————————————————————————————————
// Main Card Component
// ——————————————————————————————————————————

export function Card({ variant = "outlined", className, children, isLoading = false, layout = "default" }: CardProps) {
  return (
    <div
      className={twMerge(
        "w-full overflow-hidden",
        cardVariantStyles[variant],
        cardBaseStyles,
        layout === "flex" && "flex flex-col",
        className
      )}
      data-testid="card"
    >
      {isLoading ? (
        // Skeleton fills the card completely - respects height set on card
        <Skeleton width="100%" height="100%" className="rounded-none" />
      ) : (
        children
      )}
    </div>
  );
}

// ——————————————————————————————————————————
// Card Header
// ——————————————————————————————————————————

export function CardHeader({ title, subtitle, actions, className, isLoading = false }: CardHeaderProps) {
  if (isLoading) {
    return (
      <div className={twMerge("p-6 pb-4", className)}>
        <div className="flex justify-between items-start mb-1">
          <Skeleton width={120} height={20} />
          <Skeleton width={24} height={24} />
        </div>
        {subtitle && <Skeleton width={200} height={16} />}
      </div>
    );
  }

  if (!title && !subtitle && !actions) {
    return null;
  }

  return (
    <div className={twMerge("p-6 pb-4", className)}>
      <div className="flex justify-between items-start gap-4">
        <div className="min-w-0 flex-1">
          {title && (
            <div className="mb-1">
              {typeof title === "string" ? (
                <Heading tag="h3" size="sm" className="text-text-heading">
                  {title}
                </Heading>
              ) : (
                title
              )}
            </div>
          )}
          {subtitle && <div className="text-sm text-text-muted">{subtitle}</div>}
        </div>
        {actions && <div className="flex-shrink-0">{actions}</div>}
      </div>
    </div>
  );
}

// ——————————————————————————————————————————
// Card Media
// ——————————————————————————————————————————

export function CardMedia({ src, alt, aspectRatio = "auto", className, children, isLoading = false }: CardMediaProps) {
  if (isLoading) {
    return (
      <div className={twMerge("bg-background-muted", aspectRatioStyles[aspectRatio], className)}>
        <Skeleton width="100%" height="100%" />
      </div>
    );
  }

  if (children) {
    return <div className={twMerge("overflow-hidden", aspectRatioStyles[aspectRatio], className)}>{children}</div>;
  }

  if (!src) {
    return null;
  }

  return (
    <div className={twMerge("overflow-hidden", aspectRatioStyles[aspectRatio], className)}>
      <img src={src} alt={alt || ""} className="w-full h-full object-cover" loading="lazy" />
    </div>
  );
}

// ——————————————————————————————————————————
// Card Content
// ——————————————————————————————————————————

export function CardContent({ className, children, isLoading = false, fill = false }: CardContentProps) {
  // Extract padding classes from className - if any padding class is set, respect it
  const hasPadding = className?.match(/p(?:t|b|l|r|x|y)?-(?:\d+(?:\.\d+)?|px|\[.+?\])/);
  const defaultPadding = hasPadding ? "" : "px-6 py-4";

  if (isLoading) {
    return (
      <div className={twMerge(defaultPadding, fill && "flex-1 flex flex-col min-h-0", className)}>
        <div className="space-y-2">
          <Skeleton width="100%" height={16} />
          <Skeleton width="80%" height={16} />
          <Skeleton width="60%" height={16} />
        </div>
      </div>
    );
  }

  if (!children) {
    return null;
  }

  return (
    <div className={twMerge("px-6 py-4 text-text-body", fill && "flex-1 flex flex-col min-h-0", className)}>
      {children}
    </div>
  );
}

// ——————————————————————————————————————————
// Card Footer
// ——————————————————————————————————————————

export function CardFooter({ className, children, align = "end", isLoading = false }: CardFooterProps) {
  if (isLoading) {
    return (
      <div className={twMerge("px-6 py-4 flex gap-2 justify-end", className)}>
        <Skeleton width={80} height={32} />
        <Skeleton width={60} height={32} />
      </div>
    );
  }

  if (!children) {
    return null;
  }

  return (
    <div className={twMerge("px-6 py-4 flex items-center gap-2", footerAlignStyles[align], className)}>{children}</div>
  );
}

// ——————————————————————————————————————————
// Compound Component API
// ——————————————————————————————————————————

Card.Header = CardHeader;
Card.Media = CardMedia;
Card.Content = CardContent;
Card.Footer = CardFooter;
